import axios from 'axios'
import {setupAxios} from '../app/modules/auth'
import {API_BASE_URL} from '../utils/contants'

// =================== TYPES ===================

export interface Task {
  id: string
  projectCodeId: string
  title: string
  description?: string
  assignedToId?: string
  createdById: string
  estimatedHours?: number
  actualHours: number
  status: 'TODO' | 'IN_PROGRESS' | 'IN_REVIEW' | 'BLOCKED' | 'COMPLETED' | 'CANCELLED'
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  startDate?: string
  dueDate?: string
  completedAt?: string
  createdAt: string
  updatedAt: string
  projectCode?: {
    id: string
    code: string
    name: string
    type: string
  }
  assignedTo?: {
    id: string
    name: string
    email: string
  }
  createdBy?: {
    id: string
    name: string
  }
  _count?: {
    impediments: number
    comments: number
    attachments: number
    timesheetEntries: number
  }
}

export interface TaskComment {
  id: string
  taskId: string
  userId: string
  comment: string
  createdAt: string
  updatedAt: string
  user?: {
    id: string
    name: string
  }
}

export interface TaskAttachment {
  id: string
  taskId: string
  fileName: string
  fileUrl: string
  fileSize?: number
  mimeType?: string
  uploadedById: string
  createdAt: string
  uploadedBy?: {
    id: string
    name: string
  }
}

export interface TaskImpediment {
  id: string
  taskId: string
  title: string
  description?: string
  reportedById: string
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED'
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  resolvedAt?: string
  resolvedById?: string
  resolution?: string
  createdAt: string
  updatedAt: string
  reportedBy?: {
    id: string
    name: string
  }
  resolvedBy?: {
    id: string
    name: string
  }
}

export interface TaskStatistics {
  totalTasks: number
  byStatus: {
    TODO: number
    IN_PROGRESS: number
    IN_REVIEW: number
    BLOCKED: number
    COMPLETED: number
    CANCELLED: number
  }
  hours: {
    totalEstimated: number
    totalActual: number
  }
}

// Request types
export interface TaskCreateRequest {
  projectCodeId: string
  title: string
  description?: string
  assignedToId?: string
  estimatedHours?: number
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  startDate?: string
  dueDate?: string
}

export interface TaskUpdateRequest {
  projectCodeId?: string
  title?: string
  description?: string
  assignedToId?: string | null
  estimatedHours?: number
  status?: 'TODO' | 'IN_PROGRESS' | 'IN_REVIEW' | 'BLOCKED' | 'COMPLETED' | 'CANCELLED'
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  startDate?: string | null
  dueDate?: string | null
}

export interface TaskStatusUpdateRequest {
  status: 'TODO' | 'IN_PROGRESS' | 'IN_REVIEW' | 'BLOCKED' | 'COMPLETED' | 'CANCELLED'
  completedAt?: string | null
}

export interface TaskCommentRequest {
  comment: string
}

export interface TaskAttachmentRequest {
  fileName: string
  fileUrl: string
  fileSize?: number
  mimeType?: string
}

export interface TaskImpedimentRequest {
  title: string
  description?: string
  severity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
}

export interface ImpedimentResolutionRequest {
  resolution: string
  status: 'RESOLVED' | 'CLOSED'
}

// =================== TASK CRUD ===================

export const getTasks = async (filters?: {
  projectCodeId?: string
  assignedToId?: string
  status?: string
  priority?: string
  createdById?: string
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  if (filters?.projectCodeId) params.append('projectCodeId', filters.projectCodeId)
  if (filters?.assignedToId) params.append('assignedToId', filters.assignedToId)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.priority) params.append('priority', filters.priority)
  if (filters?.createdById) params.append('createdById', filters.createdById)

  return axios.get(`${API_BASE_URL}/tasks?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Convenience function to get tasks by project (filtered by current user)
export const getTasksByProject = async (projectCodeId: string, userId?: string) => {
  return getTasks({
    projectCodeId,
    assignedToId: userId // Filter by assigned user
  })
}

export const getTaskById = async (id: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/tasks/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getTaskStatistics = async (filters?: {
  projectCodeId?: string
  assignedToId?: string
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  if (filters?.projectCodeId) params.append('projectCodeId', filters.projectCodeId)
  if (filters?.assignedToId) params.append('assignedToId', filters.assignedToId)

  return axios.get(`${API_BASE_URL}/tasks/statistics?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const createTask = async (data: TaskCreateRequest) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/tasks`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const updateTask = async (id: string, data: TaskUpdateRequest) => {
  const {token} = setupAxios()
  return axios.put(`${API_BASE_URL}/tasks/${id}`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const updateTaskStatus = async (id: string, data: TaskStatusUpdateRequest) => {
  const {token} = setupAxios()
  return axios.patch(`${API_BASE_URL}/tasks/${id}/status`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const deleteTask = async (id: string) => {
  const {token} = setupAxios()
  return axios.delete(`${API_BASE_URL}/tasks/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== TASK COMMENTS ===================

export const getTaskComments = async (taskId: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/tasks/${taskId}/comments`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const addTaskComment = async (taskId: string, data: TaskCommentRequest) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/tasks/${taskId}/comments`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== TASK ATTACHMENTS ===================

export const getTaskAttachments = async (taskId: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/tasks/${taskId}/attachments`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const addTaskAttachment = async (taskId: string, data: TaskAttachmentRequest) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/tasks/${taskId}/attachments`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const deleteTaskAttachment = async (attachmentId: string) => {
  const {token} = setupAxios()
  return axios.delete(`${API_BASE_URL}/tasks/attachments/${attachmentId}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== TASK IMPEDIMENTS ===================

export const getTaskImpediments = async (taskId: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/tasks/${taskId}/impediments`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const addTaskImpediment = async (taskId: string, data: TaskImpedimentRequest) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/tasks/${taskId}/impediments`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const resolveTaskImpediment = async (
  impedimentId: string,
  data: ImpedimentResolutionRequest
) => {
  const {token} = setupAxios()
  return axios.patch(`${API_BASE_URL}/tasks/impediments/${impedimentId}/resolve`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

